import React, { useState } from "react";
import { useNotify } from "react-admin";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { deleteClientPermanently } from "../../../../api";
import i18n from "../../../assets/i18n";

const t = i18n.es.permanent_deletion;

/**
 * Pestaña de borrado permanente del detalle de cliente.
 *
 * Spec: `docs/features/borrado-permanente-cliente/spec.md` en la raíz multi-repo.
 *
 * La confirmación exige teclear el nombre exacto del cliente (decisión D-01 del spec). El backend
 * revalida ese mismo nombre, así que esto es la primera de dos redes, no la única.
 *
 * El montaje de esta pestaña ya está condicionado al rol en `show/index.js`; la autorización real
 * la hace el backend, que devuelve 403.
 */
const ClientPermanentDeletion = ({ record }) => {
    const notify = useNotify();

    const [isDialogOpen, setDialogOpen] = useState(false);
    const [typedName, setTypedName] = useState("");
    const [isDeleting, setDeleting] = useState(false);
    const [isLaunched, setLaunched] = useState(false);

    if (!record) {
        return null;
    }

    const clientName = record.name || "";
    const nameMatches = typedName === clientName;

    const openDialog = () => {
        setTypedName("");
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (isDeleting) {
            return;
        }
        setDialogOpen(false);
    };

    const runDeletion = async () => {
        setDeleting(true);
        try {
            // El endpoint devuelve 202: valida y lanza el borrado en un proceso aparte, porque
            // tarda del orden de 10 minutos y no cabe en el timeout del ALB. Aquí no hay informe
            // que mostrar; el resultado llega por email.
            await deleteClientPermanently(record.id, typedName);

            setDialogOpen(false);
            setLaunched(true);
            notify(t.launched, { type: "success", autoHideDuration: 15000 });
        } catch (error) {
            // 409 con un borrado ya en marcha tiene su propio mensaje: reintentar no arregla nada.
            const alreadyRunning =
                error && 409 === error.status && /already running/i.test(error.message || "");

            const message = alreadyRunning
                ? t.already_running
                : error && error.status
                    ? `${t.error} ${error.message || ""}`.trim()
                    : t.error_timeout;

            notify(message, {
                type: alreadyRunning ? "warning" : "error",
                autoHideDuration: 15000,
            });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 720, py: 1 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>{t.warning_title}</AlertTitle>
                {t.warning_body}
            </Alert>

            <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{clientName}</strong>
                <br />
                {record.id}
            </Typography>

            <Button
                variant="contained"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={openDialog}
                disabled={isLaunched}
            >
                {t.button}
            </Button>

            {isLaunched && (
                <Alert severity="info" sx={{ mt: 3 }}>
                    <AlertTitle>{t.launched}</AlertTitle>
                    {t.launched_detail}
                </Alert>
            )}

            <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t.dialog_title}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {t.dialog_intro}
                        <br />
                        <strong>{clientName}</strong> — {record.id}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t.dialog_confirm_label}
                        value={typedName}
                        disabled={isDeleting}
                        onChange={(event) => setTypedName(event.target.value)}
                        error={typedName.length > 0 && !nameMatches}
                        helperText={typedName.length > 0 && !nameMatches ? t.dialog_mismatch : " "}
                    />
                    {isDeleting && (
                        <DialogContentText sx={{ mt: 1 }}>{t.in_progress}</DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={isDeleting}>
                        {t.cancel}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={runDeletion}
                        disabled={!nameMatches || isDeleting}
                    >
                        {t.confirm}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClientPermanentDeletion;
